import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { createFileRoute } from "@tanstack/react-router";
import { GhostIcon } from "lucide-react";
import { parseAsFloat, useQueryState } from "nuqs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSpinDelay } from "spin-delay";

import { InvoiceViewer } from "@/components/shared/invoice-review/invoice-viewer";
import InvoiceProvider from "@/features/invoice-standard-review/accounting/ReviewProvider";
import { useInvoicePdfFullscreenSync } from "@/hooks/use-invoice-pdf-sync";

export const Route = createFileRoute("/_authenticated/$company/doppelansicht")({
  component: InvoicePdfViewer,
});

const A4_ASPECT_RATIO = Math.SQRT2;

function InvoicePdfViewer() {
  const { t } = useTranslation();
  const { invoiceId, viewState } = useInvoicePdfFullscreenSync();
  const [zoom, setZoom] = useQueryState("zoom", parseAsFloat.withDefault(1));

  const [maxWidth, setMaxWidth] = useState(720);

  useEffect(() => {
    const calculateMaxWidth = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;

      const availableHeight = vh - 48;

      const heightBasedWidth = availableHeight / A4_ASPECT_RATIO;

      const availableWidth = vw - 24;

      setMaxWidth(Math.min(heightBasedWidth, availableWidth));
    };

    calculateMaxWidth();
    window.addEventListener("resize", calculateMaxWidth);
    return () => window.removeEventListener("resize", calculateMaxWidth);
  }, []);

  const showEmptyState = useSpinDelay(!invoiceId || !viewState.pdfBlobData, {
    delay: 300,
    minDuration: 0,
  });

  if (showEmptyState) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ backgroundColor: "#e5e6eb" }}
      >
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <GhostIcon />
            </EmptyMedia>
            <EmptyTitle>{t("invoice_pdf_fullscreen_empty_title")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (!invoiceId || !viewState.pdfBlobData) {
    return (
      <div
        className="h-full bg-gray-200"
        style={{ backgroundColor: "#e5e6eb" }}
      />
    );
  }

  return (
    <div className="flex min-h-screen w-full">
      <main className="flex-1 relative w-full h-screen overflow-hidden">
        <InvoiceProvider invoiceId={invoiceId}>
          <div id="invoice-pdf-viewer-screen" className="h-screen">
            <div className="h-full" style={{ backgroundColor: "#e5e6eb" }}>
              <InvoiceViewer
                isFullscreen
                invoiceId={invoiceId}
                canvasEnabled={false}
                pdfBlob={viewState.pdfBlobData}
                coordinates={viewState.coordinates}
                maxWidth={maxWidth}
                zoom={zoom}
                onZoomChange={setZoom}
              />
            </div>
          </div>
        </InvoiceProvider>
      </main>
    </div>
  );
}
